/*******************************************************************************
 * Copyright     : Copyright (c)  2020 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc., and its use or disclosure in whole or in
 *                 part without express written permission of Honeywell Inc.
 *                 is prohibited.
 *
 * Name          : CommonHeader.tsx
 *
 * Project Title : APM Settings Tool
 *
 * Description   : Common Header component
 *
 * Company       : Honeywell Technology Solutions , Hyderabad
 *
 * @Author       : H422335
 *
 * @Date         : January 17, 2022
 *
 * @Version      : Version 1.0
 *
 *
 ******************************************************************************/

import React, { FC, useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Accordion, Tab, Grid } from "@scuf/common";
import { selectCustomer, fetchCustomers } from "stores/CustomerStore";
import { resetGeneralInfoForm } from "stores/GeneralInfoStore";
import { resetSelectedApmFile } from "stores/SelectApmFileStore";
import { resetSelectedAircraftDetails } from "stores/AircraftDetailsStore";
import { resetAPMSettingsStore } from "stores/ApmSettingsStore";
import {
  selectErrorNotification,
  setErrorNotification,
  setShowAccountModal,
  selectShowAccountModal,
  setDiscardChangesWarning,
} from "stores/UIStore";
import {
  setCurrentWorkFlowType,
  setCurrentWorkFlowStep,
  selectCurrentWorkFlowType,
  selectCurrentWorkFlowStep,
  resetSelectedApmRecord,
} from "stores/ApmWorkFlowStore";
import { HeaderBanner } from "components/HeaderBanner";
import { ErrorNotification } from "components/ErrorNotification";
import { AccountModal } from "components/AccountModal";
import {
  EnumApmSettingsWorkFlow,
  EnumHeaderAction,
} from "models/configuration";
import { LABELS } from "./constants";
import "./CommonHeader.scss";

export const CommonHeader: FC = ({ children }) => {
  const [seletedtab, setSeletedtab] = useState(0);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { error } = useSelector(selectErrorNotification);
  const selectedCustomer = useSelector(selectCustomer);
  const showAccountModal = useSelector(selectShowAccountModal);
  const currentWorkFlowType = useSelector(selectCurrentWorkFlowType);
  const currentWorkFlowStep = useSelector(selectCurrentWorkFlowStep);

  const resetStoreValuesForNewRecord = useCallback(() => {
    dispatch(resetGeneralInfoForm());
    dispatch(resetSelectedApmFile());
    dispatch(resetSelectedAircraftDetails());
    dispatch(resetAPMSettingsStore());
    dispatch(resetSelectedApmRecord());
  }, [dispatch]);

  const discardWorkFlowChangesAndNavigate = (
    navigateAction: EnumHeaderAction
  ) => {
    if (
      (currentWorkFlowType === EnumApmSettingsWorkFlow.New ||
        currentWorkFlowType === EnumApmSettingsWorkFlow.Edit) &&
      currentWorkFlowStep !== 0
    ) {
      dispatch(
        setDiscardChangesWarning({
          showWarningModal: true,
          headerAction: navigateAction,
        })
      );
    } else {
      switch (navigateAction) {
        case EnumHeaderAction.apmHistory:
          navigate("/");
          break;
        case EnumHeaderAction.createNewApm:
          resetStoreValuesForNewRecord();
          dispatch(setCurrentWorkFlowType(EnumApmSettingsWorkFlow.New));
          navigate("new");
          dispatch(setCurrentWorkFlowStep(1));
          break;
        case EnumHeaderAction.customerChange:
          dispatch(setShowAccountModal(true));
          break;
      }
    }
  };

  const onTabChangeHandler = (activeIndex: number) => {
    setSeletedtab(activeIndex);
    dispatch(setErrorNotification(null));
    if (activeIndex === 0) {
      discardWorkFlowChangesAndNavigate(EnumHeaderAction.apmHistory);
    } else {
      discardWorkFlowChangesAndNavigate(EnumHeaderAction.createNewApm);
    }
  };

  const selectCustomerHandler = () => {
    discardWorkFlowChangesAndNavigate(EnumHeaderAction.customerChange);
  };

  useEffect(() => {
    dispatch(fetchCustomers());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid>
      <Grid.Row>
        <Grid.Column className="col-no-padding">
          <HeaderBanner />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column className="col-no-padding">
          <div className="common-header">
            <div className="select-customer">
              <Accordion>
                <Accordion.Content
                  onClick={selectCustomerHandler}
                  active={false}
                  title={
                    selectedCustomer !== null
                      ? selectedCustomer?.customerName
                      : "Select Account"
                  }
                />
              </Accordion>
            </div>
            <Tab
              activeIndex={seletedtab}
              className={seletedtab === 1 ? "tab-create-apm" : "tab"}
              onTabChange={onTabChangeHandler}
            >
              <Tab.Pane
                title={LABELS.configTabTitle}
                className="tab-pane"
                disabled={selectedCustomer === null}
              >
                <ErrorNotification error={error} />
                {children}
              </Tab.Pane>

              <Tab.Pane
                title={LABELS.createNewAPMFile}
                className="tab-pane"
                disabled={selectedCustomer === null}
              >
                <ErrorNotification error={error} />
                {children}
              </Tab.Pane>
            </Tab>
          </div>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column className="col-no-padding">
          {showAccountModal && <AccountModal />}
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};
